'use client';

import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import React, { useEffect, ReactElement } from 'react';
import { I18nextProvider as Provider, initReactI18next } from 'react-i18next';
import { useOrgContextOptional } from '@provider-directory/components/org-context/org-context-provider';
import {
  ContextKeySchema,
  ClientOrgContextKey,
} from '@provider-directory/types/organizations.contracts';
import transEn from './locales/en/translation.json';
import meacEn from './locales/en/translation_meac.json';
import mmcEn from './locales/en/translation_mmc.json';
import wphEn from './locales/en/translation_wph.json';
import transEs from './locales/es/translation.json';
import meacEs from './locales/es/translation_meac.json';
import mmcEs from './locales/es/translation_mmc.json';
import wphEs from './locales/es/translation_wph.json';

export const resources = {
  en: {
    translation: transEn,
    [ContextKeySchema.enum.montefiore_mmc]: mmcEn,
    [ContextKeySchema.enum.montefiore_wph]: wphEn,
    [ContextKeySchema.enum.montefiore_meac]: meacEn,
  },
  es: {
    translation: transEs,
    [ContextKeySchema.enum.montefiore_mmc]: mmcEs,
    [ContextKeySchema.enum.montefiore_wph]: wphEs,
    [ContextKeySchema.enum.montefiore_meac]: meacEs,
  },
};

// Static bundles keyed by directory `contextKey`. `I18nProvider` aliases the
// active directory's bundle under the BE-supplied
// `runtimeConfig.translationNamespace` name so client lookups can use the same
// namespace the server uses. Today the BE name must correspond
// to a bundle already present in the FE build — a known limitation while
// translation files ship in the FE bundle. The structure is preparation for
// moving translation files to external hosting (e.g. S3): once bundles are
// fetched at runtime, the same `translationNamespace` will identify a remote
// resource and this aliasing layer will be removed.
const directoryBundlesByContextKey: Record<
  ClientOrgContextKey,
  { en: object; es: object } | undefined
> = {
  [ContextKeySchema.enum.montefiore_mmc]: { en: mmcEn, es: mmcEs },
  [ContextKeySchema.enum.montefiore_wph]: { en: wphEn, es: wphEs },
  [ContextKeySchema.enum.montefiore_meac]: { en: meacEn, es: meacEs },
  [ContextKeySchema.enum.acme]: undefined,
};

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    fallbackLng: 'en',
    fallbackNS: 'translation',
    detection: {
      order: ['querystring', 'cookie'],
      lookupQuerystring: 'l',
      caches: ['cookie'],
    },
    resources,
  });

export default i18next;

interface I18nProviderProps {
  children: React.ReactNode;
  language: string;
}

export const I18nProvider = ({
  children,
  language,
}: I18nProviderProps): ReactElement => {
  const runtimeConfig = useOrgContextOptional()?.runtimeConfig;
  const contextKey = runtimeConfig?.contextKey;
  const translationNamespace = runtimeConfig?.translationNamespace;

  useEffect(() => {
    void i18next.changeLanguage(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!contextKey || !translationNamespace) {
      return;
    }
    const bundles = directoryBundlesByContextKey[contextKey];
    if (!bundles) {
      return;
    }
    // `addResourceBundle(... deep=true, overwrite=true)` is idempotent — safe
    // to re-run if either contextKey or translationNamespace changes.
    i18next.addResourceBundle(
      'en',
      translationNamespace,
      bundles.en,
      true,
      true,
    );
    i18next.addResourceBundle(
      'es',
      translationNamespace,
      bundles.es,
      true,
      true,
    );
  }, [contextKey, translationNamespace]);

  return <Provider i18n={i18next}>{children}</Provider>;
};
